import React, { useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { DriverDetails } from '../../types/predictions';
import { SegmentedToggle } from '../SegmentedToggle';
import { driverColors, raceKey, useChartTokens } from './chartKit';

interface Props {
  driversDetails: Record<string, DriverDetails[]>;
}

interface Record2 {
  label: string;
  a: number;
  b: number;
  /** Color of the "b" side (the opponent driver, or neutral gray for the group). */
  bColor?: string;
  /** Name of the "b" side for the tooltip. */
  bName?: string;
  /** Group split: the "b" races broken down by which driver finished best of the rest. */
  bParts?: { name: string; count: number; color?: string }[];
}

/** The "vs" choice that compares one driver with every other driver in the matchup. */
const GROUP = '__group';
/** The picked driver's result and the best-placed opponent's (bDriver) in one shared race. */
type Pair = { ra: DriverDetails; rb: DriverDetails; bDriver: string };

/**
 * Head-to-head record in races both drivers ran (who finished ahead), overall and by track. With 3+ drivers, "vs" can
 * be the whole group: a race counts for the driver only when they finished ahead of every other group driver in it.
 */
export const HeadToHeadChart: React.FC<Props> = ({ driversDetails }) => {
  const tokens = useChartTokens();
  const drivers = Object.keys(driversDetails);
  const colors = driverColors(drivers, tokens.series);
  const [a, setA] = useState(drivers[0]);
  const canGroup = drivers.length > 2;
  const [b, setB] = useState(canGroup ? GROUP : drivers[1]);
  const [breakdown, setBreakdown] = useState<'track' | 'driver'>('track');
  const [splitGroup, setSplitGroup] = useState(false);
  const isGroup = b === GROUP;
  const groupColor = tokens.dark ? '#8a8a8a' : '#9e9e9e';
  const bLabel = isGroup ? 'the rest of the group' : b;

  // Pairs of (driver, opponent) results in shared races. For the group, the opponent is the best-placed other driver.
  const pairsVs = useMemo(() => {
    const index = (d: string) => new Map((driversDetails[d] ?? []).map((r) => [raceKey(r), r]));
    return (opponents: string[]): Pair[] => {
      const maps = opponents.map(index);
      return (driversDetails[a] ?? []).flatMap((ra) => {
        const others = maps
          .map((m, i) => ({ r: m.get(raceKey(ra)), d: opponents[i] }))
          .filter((o): o is { r: DriverDetails; d: string } => !!o.r);
        if (others.length === 0) return [];
        const best = others.reduce((x, o) => (o.r.Pos < x.r.Pos ? o : x));
        return [{ ra, rb: best.r, bDriver: best.d }];
      });
    };
  }, [driversDetails, a]);

  const { overall, rows, shared } = useMemo(() => {
    const tally = (list: Pair[]): [number, number] => [
      list.filter((x) => x.ra.Pos < x.rb.Pos).length,
      list.filter((x) => x.rb.Pos < x.ra.Pos).length,
    ];
    const opponents = isGroup ? drivers.filter((d) => d !== a) : [b];
    const split = isGroup && breakdown === 'track' && splitGroup;
    // Races lost to the group, credited to whichever group driver finished best in each.
    const parts = (list: Pair[]) =>
      split
        ? opponents.map((d) => ({
            name: d,
            count: list.filter((x) => x.rb.Pos < x.ra.Pos && x.bDriver === d).length,
            color: colors.get(d),
          }))
        : undefined;
    const all = pairsVs(opponents);
    const [oa, ob] = tally(all);
    const overall: Record2 = {
      label: isGroup ? 'vs whole group' : 'All shared races',
      a: oa,
      b: ob,
      bColor: isGroup ? groupColor : colors.get(b),
      bName: bLabel,
      bParts: parts(all),
    };
    let rows: Record2[];
    if (isGroup && breakdown === 'driver') {
      // One driver-vs-driver bar per other group driver.
      rows = opponents.map((d) => {
        const [ta, tb] = tally(pairsVs([d]));
        return { label: `vs ${d}`, a: ta, b: tb, bColor: colors.get(d), bName: d };
      });
    } else {
      const tracks = new Map<string, Pair[]>();
      all.forEach((x) => tracks.set(x.ra.Site, [...(tracks.get(x.ra.Site) ?? []), x]));
      rows = Array.from(tracks.entries())
        .filter(([, list]) => list.length >= 2)
        .sort((p, q) => q[1].length - p[1].length)
        .slice(0, 8)
        .map(([label, list]) => {
          const [ta, tb] = tally(list);
          return { label, a: ta, b: tb, bColor: overall.bColor, bName: bLabel, bParts: parts(list) };
        });
    }
    return { overall, rows, shared: all.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairsVs, a, b, isGroup, breakdown, splitGroup, groupColor, tokens.series]);

  const pick = (value: string, set: (v: string) => void, other: string, label: string, group = false) => (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      onChange={(e) => set(e.target.value)}
      sx={{ minWidth: 180 }}
    >
      {group && <MenuItem value={GROUP}>Whole group</MenuItem>}
      {drivers
        .filter((d) => d !== other)
        .map((d) => (
          <MenuItem key={d} value={d}>
            {d}
          </MenuItem>
        ))}
    </TextField>
  );

  const bar = (r: Record2, strong = false) => {
    const total = r.a + r.b;
    return (
      <Tooltip
        key={r.label}
        title={
          r.bParts
            ? `${r.label}: ${a} ahead of everyone ${r.a}; ${r.bParts.map((p) => `${p.name} best ${p.count}`).join(', ')} (of ${total} race${total === 1 ? '' : 's'})`
            : `${r.label}: ${a} ahead ${r.a}, ${r.bName ?? b} ahead ${r.b} (of ${total} race${total === 1 ? '' : 's'})`
        }
        placement="top"
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 56px', sm: '140px 1fr 64px' },
            alignItems: 'center',
            gap: 1,
            py: 0.5,
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: strong ? 600 : 400, gridColumn: { xs: '1 / -1', sm: 'auto' } }}
          >
            {r.label}
          </Typography>
          {/* Part-to-whole: segments with a 2px surface gap; 4px rounded outer ends. */}
          <Box
            sx={{
              display: 'flex',
              height: 14,
              gap: '2px',
              '& > :first-of-type': { borderTopLeftRadius: 4, borderBottomLeftRadius: 4 },
              '& > :last-of-type': { borderTopRightRadius: 4, borderBottomRightRadius: 4 },
            }}
          >
            {[{ count: r.a, color: colors.get(a) }, ...(r.bParts ?? [{ count: r.b, color: r.bColor }])]
              .filter((seg) => seg.count > 0)
              .map((seg, i) => (
                <Box key={i} sx={{ flex: seg.count, bgcolor: seg.color }} />
              ))}
          </Box>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
            {r.a}–{r.b}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box>
      {drivers.length > 2 && (
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          {pick(a, setA, b, 'Driver')}
          {pick(b, setB, a, 'vs', canGroup)}
          {isGroup && (
            <SegmentedToggle
              aria-label="Break down by"
              size="small"
              value={breakdown}
              onChange={setBreakdown}
              options={[
                { value: 'track', label: 'By track' },
                { value: 'driver', label: 'By driver' },
              ]}
            />
          )}
          {isGroup && breakdown === 'track' && (
            <FormControlLabel
              control={<Checkbox size="small" checked={splitGroup} onChange={(e) => setSplitGroup(e.target.checked)} />}
              label="Split the group by driver"
              slotProps={{ typography: { variant: 'body2' } }}
            />
          )}
        </Stack>
      )}
      {/* Legend (identity is never color alone: the record is also written out). In the by-driver breakdown each bar's
          opponent is named in its label and keeps that driver's color. */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.get(a) }} />
          <Typography variant="body2">{isGroup ? `${a} ahead of everyone` : `${a} finished ahead`}</Typography>
        </Stack>
        {overall.bParts ? (
          overall.bParts.map((p) => (
            <Stack key={p.name} direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color }} />
              <Typography variant="body2">{p.name} best</Typography>
            </Stack>
          ))
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: overall.bColor }} />
            <Typography variant="body2">{isGroup ? 'someone else ahead' : `${b} finished ahead`}</Typography>
          </Stack>
        )}
      </Stack>
      {shared === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
          These drivers haven't run any of the same races behind their ratings.
        </Typography>
      ) : (
        <>
          {bar(overall, true)}
          {rows.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {isGroup && breakdown === 'driver'
                ? `${a} against each driver (races both ran)`
                : 'By track (tracks with at least 2 shared races)'}
            </Typography>
          )}
          {rows.map((r) => bar(r))}
        </>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {isGroup
          ? `Races ${a} and at least one other group driver ran, among those behind their ratings. ${a} wins a race by finishing ahead of every other group driver in it.${overall.bParts ? ' Split: each race lost goes to the group driver who finished best in it.' : ''}`
          : 'Races both drivers ran among those behind their ratings; whoever finished higher wins the race.'}
      </Typography>
    </Box>
  );
};
